import {
  useDeleteUserWorkspaceFcmTokenMutation,
  useGetWalletDetailsV2Query,
  useworkspaceAuthMeV2Query,
  VerifyPaymentRequestMutationVariables,
  Workspace,
} from '@/types/generated/graphql';
import { useApolloClient } from '@apollo/client';
import { centsToDollar } from '@modules/settings/subscription/utils/utils';
import { cacheOnly, COOKIE_KEY } from '@shared/constants';
import { broadcastAuthChange } from '@shared/hooks/use-auth-state-change';
import { useWorkSpaceSecret } from '@shared/hooks/use-workspace-secret';
import { cookieOptions } from '@shared/utils';
import { notification } from 'antd';
import Cookies from 'js-cookie';
import { deleteToken as deleteFirebaseToken } from 'firebase/messaging';
import { messaging } from '@/lib/firebase/get-firebase-messaging-instance';

export function useWorkSpaceSwitch() {
  const { data: authMeWorkspace } = useworkspaceAuthMeV2Query(cacheOnly);
  const wallet = useGetWalletDetailsV2Query({ fetchPolicy: 'cache-and-network' });
  const { workspaceLogin, loading } = useWorkSpaceSecret();
  const client = useApolloClient();
  const amount = wallet?.data?.getWalletDetailsV2?.data?.amount as number;
  const dollarAmount = centsToDollar(amount);
  const [deleteServerFCM] = useDeleteUserWorkspaceFcmTokenMutation();

  const handlePaymentWorkspaceSwitch = async (
    workspaceId: Workspace['id'],
    urlToken: VerifyPaymentRequestMutationVariables['urlToken']
  ) => {
    if (loading) return;
    if (authMeWorkspace?.workspaceAuthMeV2?.data?.id === workspaceId) return;

    await workspaceLogin({
      variables: {
        input: {
          workspaceId,
        },
      },
      onError: (error) => {
        notification.error({ message: error?.message });
      },
    }).then((data) => {
      const token = data?.data?.workspaceLogin?.data?.token?.access;
      if (token) {
        Cookies.set(COOKIE_KEY.workspaceSecret, String(token), cookieOptions);
      }
      notification.success({ message: data?.data?.workspaceLogin?.message });
      client.clearStore();
      window.location.href = `/payment-request?token=${urlToken}`;
    });
  };

  const handleWorkspaceSwitch = async (workspaceId: Workspace['id']) => {
    if (loading) return;
    await workspaceLogin({
      variables: {
        input: {
          workspaceId,
        },
      },
      onCompleted: async (data) => {
        const token = data?.workspaceLogin?.data?.token?.access;
        if (token) {
          Cookies.set(COOKIE_KEY.workspaceSecret, String(token), cookieOptions);
        }

        if (messaging) {
          await deleteServerFCM();
          await deleteFirebaseToken(messaging);
        }

        // always keep this statement last
        broadcastAuthChange();
        notification.success({ message: data.workspaceLogin?.message });
      },
      onError: (error) => {
        notification.error({ message: error?.message });
      },
    });
  };

  return {
    authMeWorkspace,
    dollarAmount,
    handleWorkspaceSwitch,
    handlePaymentWorkspaceSwitch,
    loading: loading || wallet?.loading,
  };
}
